/** @jsx jsx */
import { FullScreenModal, FullScreenModalContainer, FullScreenModalProps } from "@eworkit/iris";
import React, { FC, useCallback, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { WizardProvider } from "@/components/wizards/WizardContext";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { IJobApplication } from "@/data/job-applications";
import { ExitModalActionType } from "@/data/job-applications/types/ExitModalActionType";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { refresh } from "@/data/session";

import { ApplyExitModal } from "./_components/ApplyExitModal";
import { ApplyModalHeader } from "./_components/ApplyModalHeader";
import { ApplyWizardProvider, useApplyWizard } from "./_components/ApplyWizardContext";
import { ApplyWizard } from "./ApplyWizard";
import { useApplyWizardConfig } from "./useApplyWizardConfig";

interface ApplyModalComponentProps extends FullScreenModalProps {
  jobRequest: JobRequest;
  onClosed?: (action?: ExitModalActionType) => void;
}

const ApplyModalComponent: FC<ApplyModalComponentProps> = ({ jobRequest, onClosed, ...modalProps }) => {
  const { t } = useTranslation("session");

  const { updateContextUser } = useContext(UserContext);

  const { profile, draft, currentStep, initialProfile } = useApplyWizard();

  const header = t("JOB_REQUESTS.APPLY.APPLY_TO", { jobRequestName: jobRequest.title });

  const exitModalState = useDialogState({ animated: true });

  const modalRef = useRef<HTMLDivElement>(null);

  const newModalProps = {
    ...modalProps,
    dialogState: {
      ...modalProps.dialogState,
      hide: async () => {
        if (draft?.status === "SUBMITTED") {
          if (initialProfile) {
            const user = await refresh();
            updateContextUser(user);
          }
          modalProps.dialogState.hide();
          onClosed?.("SUBMITTED");
        } else if (profile && draft) {
          exitModalState.show();
        } else {
          modalProps.dialogState.hide();
          onClosed?.();
        }
      },
    },
  };

  const handleViewThisApplicationButtonClick = useCallback(() => {
    onClosed?.("SUBMITTED");
  }, [onClosed]);

  const handleSaved = useCallback(() => {
    modalProps.dialogState.hide();
    onClosed?.("SAVED");
  }, [modalProps, onClosed]);

  const handleRemoved = useCallback(() => {
    modalProps.dialogState.hide();
    onClosed?.("REMOVED");
  }, [modalProps, onClosed]);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.scrollTo(0, 0);
    }
  }, [modalRef, currentStep]);

  return (
    <React.Fragment>
      <FullScreenModal {...newModalProps} aria-label={header} ref={modalRef}>
        <FullScreenModalContainer>
          <ApplyModalHeader jobRequest={jobRequest} header={header} onCloseClick={newModalProps.dialogState.hide} />
          <ApplyWizard
            jobRequest={jobRequest}
            modalRef={modalRef}
            onViewThisApplicationClick={handleViewThisApplicationButtonClick}
          />
        </FullScreenModalContainer>
      </FullScreenModal>

      <ApplyExitModal dialogState={exitModalState} onSaved={handleSaved} onRemoved={handleRemoved} />
    </React.Fragment>
  );
};

interface ApplyModalProps extends FullScreenModalProps {
  jobRequest: JobRequest;
  initialDraft?: IJobApplication;
  designatedCandidate?: string;
  initialProfile?: IConsultantProfile;
  onClosed?: (action?: ExitModalActionType) => void;
}

export const ApplyModal: FC<ApplyModalProps> = ({
  jobRequest,
  initialDraft,
  designatedCandidate,
  initialProfile,
  onClosed,
  ...modalProps
}) => {
  const config = useApplyWizardConfig(jobRequest, initialDraft);

  return (
    <ApplyWizardProvider
      jobRequest={jobRequest}
      initialDraft={initialDraft}
      designatedCandidate={designatedCandidate}
      initialProfile={initialProfile}>
      <WizardProvider config={config}>
        <ApplyModalComponent jobRequest={jobRequest} onClosed={onClosed} {...modalProps} />
      </WizardProvider>
    </ApplyWizardProvider>
  );
};
